// src/pages/admin/Page_LeadDetail/Page_LeadDetail.js
//
// F6 M3 - Detalle de un lead.
//
// 3 zonas:
//   - Cabecera: nombre, badge status, acciones (cambiar status, mailto).
//   - Datos: contacto, centro, UTMs, mensaje, referrer/landing/IP.
//   - Timeline: notas ordenadas desc + textarea para anadir.

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Tag,
  Select,
  Button,
  Input,
  Empty,
  Spin,
  message,
  Divider,
  Table,
  Popconfirm,
} from "antd";
import {
  ArrowLeftOutlined,
  MailOutlined,
  PhoneOutlined,
  SendOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../../../hooks/useAuth";
import { leadsApi } from "../../../api/leadsApi";
import { PageHeader } from "../../../ui";
import "./Page_LeadDetail.scss";

const { TextArea } = Input;

const STATUS_COLOR = {
  new: "blue",
  contacted: "cyan",
  qualified: "geekblue",
  demo_scheduled: "purple",
  proposal_sent: "gold",
  converted: "green",
  discarded: "default",
};

const STATUS_LABEL = {
  new: "Nuevo",
  contacted: "Contactado",
  qualified: "Cualificado",
  demo_scheduled: "Demo agendada",
  proposal_sent: "Propuesta enviada",
  converted: "Convertido",
  discarded: "Descartado",
};

const STATUS_OPTIONS = Object.entries(STATUS_LABEL).map(([value, label]) => ({
  value,
  label,
}));

// Drip M3.1 - estado de cada correo de nurture registrado (EmailLog.status).
const EMAIL_STATUS = {
  sent: { color: "green", label: "Enviado" },
  failed: { color: "red", label: "Fallo" },
  skipped: { color: "default", label: "Omitido" },
  dry_run: { color: "blue", label: "Simulado" },
};

// Drip M3.2 - etiqueta legible de cada paso (la clave viene del backend).
const STEP_LABELS = {
  hype: "Bienvenida (hype)",
  activacion: "Activacion",
  oferta: "Oferta 30%",
};

const EMAIL_COLUMNS = [
  { title: "Paso", dataIndex: "step", key: "step", width: 56 },
  {
    title: "Plantilla",
    dataIndex: "templateKey",
    key: "templateKey",
    width: 110,
    render: (v) => v || "—",
  },
  {
    title: "Asunto",
    dataIndex: "subject",
    key: "subject",
    ellipsis: true,
    render: (v) => v || "—",
  },
  {
    title: "Estado",
    dataIndex: "status",
    key: "status",
    width: 100,
    render: (v) => {
      const s = EMAIL_STATUS[v] || { color: "default", label: v };
      return <Tag color={s.color}>{s.label}</Tag>;
    },
  },
  {
    title: "Fecha",
    dataIndex: "sent_at",
    key: "sent_at",
    width: 150,
    render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"),
  },
];

function Field({ label, children }) {
  return (
    <div className="lead-field">
      <div className="lead-field__label">{label}</div>
      <div className="lead-field__value">{children || "—"}</div>
    </div>
  );
}

export function Page_LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [sendingNote, setSendingNote] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState([]);
  // Drip M3.1 - correos de nurture enviados a este lead + estado del drip.
  const [emails, setEmails] = useState([]);
  const [dripState, setDripState] = useState(null);
  const [emailsLoading, setEmailsLoading] = useState(true);
  // Drip M3.2 - control por lead.
  const [steps, setSteps] = useState([]);
  const [selectedStep, setSelectedStep] = useState(null);
  const [pausing, setPausing] = useState(false);
  const [sending, setSending] = useState(false);
  const [consenting, setConsenting] = useState(false);

  const fetchLead = useCallback(async () => {
    if (!accessToken || !id) return;
    setLoading(true);
    try {
      const res = await leadsApi.get(accessToken, id);
      setLead(res.data);
    } catch (err) {
      message.error(`Error cargando lead: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    fetchLead();
  }, [fetchLead]);

  // Drip M3.1 - log de correos + estado del drip. No bloqueante: si falla, el
  // detalle del lead sigue visible.
  const fetchEmails = useCallback(async () => {
    if (!accessToken || !id) return;
    setEmailsLoading(true);
    try {
      const res = await leadsApi.listLeadEmails(accessToken, id);
      setEmails(res?.data?.emails || []);
      setDripState(res?.data?.drip || null);
      const stepsArr = res?.data?.steps || [];
      setSteps(stepsArr);
      setSelectedStep((prev) => prev ?? stepsArr[0]?.step ?? null);
    } catch (err) {
      // silencioso: no rompe la vista del lead
    } finally {
      setEmailsLoading(false);
    }
  }, [accessToken, id]);

  // Drip M3.2 - pausar/reanudar el drip del lead.
  const handleTogglePause = useCallback(async () => {
    if (!dripState?.hasUser) return;
    setPausing(true);
    try {
      const res = await leadsApi.setDripPaused(
        accessToken,
        id,
        !dripState.drip_paused,
      );
      setDripState(res?.data?.drip || null);
      message.success(res?.message || "Actualizado");
    } catch (err) {
      message.error(err.message || "Error");
    } finally {
      setPausing(false);
    }
  }, [accessToken, id, dripState]);

  // Drip M5 - activar el consentimiento de marketing desde el admin (el
  // backend lo bloquea con 409 si hubo baja explicita).
  const handleActivateConsent = useCallback(async () => {
    if (!dripState?.hasUser) return;
    setConsenting(true);
    try {
      const res = await leadsApi.setDripConsent(accessToken, id, true);
      setDripState(res?.data?.drip || null);
      message.success(res?.message || "Consentimiento activado");
    } catch (err) {
      message.error(err.message || "No se pudo activar el consentimiento");
    } finally {
      setConsenting(false);
    }
  }, [accessToken, id, dripState]);

  // Drip M3.2 - enviar un paso ahora (manual). Refresca el log al terminar.
  const handleSendNow = useCallback(async () => {
    if (!selectedStep) return;
    setSending(true);
    try {
      const res = await leadsApi.sendDripNow(accessToken, id, selectedStep);
      message.success(res?.message || "Enviado");
      fetchEmails();
    } catch (err) {
      message.error(err.message || "No se pudo enviar");
    } finally {
      setSending(false);
    }
  }, [accessToken, id, selectedStep, fetchEmails]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  // F6 M5 - users a los que se puede asignar.
  useEffect(() => {
    if (!accessToken) return;
    leadsApi
      .listAssignableUsers(accessToken)
      .then((res) => setAssignableUsers(res?.data?.items || []))
      .catch(() => {});
  }, [accessToken]);

  const handleStatusChange = useCallback(
    async (newStatus) => {
      try {
        const res = await leadsApi.patch(accessToken, id, {
          status: newStatus,
        });
        setLead(res.data);
        message.success(`Estado actualizado a ${STATUS_LABEL[newStatus]}`);
      } catch (err) {
        message.error(`Error: ${err.message}`);
      }
    },
    [accessToken, id],
  );

  const handleAssignChange = useCallback(
    async (userIdOrNull) => {
      try {
        const res = await leadsApi.patch(accessToken, id, {
          assigned_to: userIdOrNull,
        });
        setLead(res.data);
        message.success(userIdOrNull ? "Lead asignado" : "Lead desasignado");
      } catch (err) {
        message.error(`Error: ${err.message}`);
      }
    },
    [accessToken, id],
  );

  const handleAddNote = useCallback(async () => {
    const trimmed = noteText.trim();
    if (!trimmed) return;
    setSendingNote(true);
    try {
      const res = await leadsApi.addNote(accessToken, id, trimmed);
      setLead(res.data);
      setNoteText("");
      message.success("Nota anadida");
    } catch (err) {
      message.error(`Error: ${err.message}`);
    } finally {
      setSendingNote(false);
    }
  }, [accessToken, id, noteText]);

  if (loading) {
    return (
      <div className="page-lead-detail page-lead-detail--loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="page-lead-detail">
        <Empty description="Lead no encontrado" />
        <Button type="link" onClick={() => navigate("/admin/leads")}>
          Volver al listado
        </Button>
      </div>
    );
  }

  // Notas ordenadas desc (las nuevas arriba).
  const notesDesc = (lead.notes || [])
    .slice()
    .sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

  const utm = lead.utm || {};
  const hasUtm = Object.values(utm).some((v) => v);

  return (
    <div className="page-lead-detail">
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate("/admin/leads")}
        style={{ paddingLeft: 0, marginBottom: 8 }}
      >
        Volver al listado
      </Button>

      <PageHeader
        title={lead.centerName || lead.name || lead.email}
        subtitle={
          <span>
            Recibido el{" "}
            <strong>{dayjs(lead.created_at).format("DD/MM/YYYY HH:mm")}</strong>{" "}
            · vertical <Tag>{lead.vertical || "—"}</Tag> · fuente{" "}
            <Tag>{lead.source || "—"}</Tag>
          </span>
        }
      />

      <div className="page-lead-detail__top">
        <Tag color={STATUS_COLOR[lead.status]} style={{ fontSize: 14 }}>
          {STATUS_LABEL[lead.status] || lead.status}
        </Tag>
        <Select
          value={lead.status}
          options={STATUS_OPTIONS}
          onChange={handleStatusChange}
          style={{ minWidth: 220 }}
        />
        {lead.email && (
          <Button
            icon={<MailOutlined />}
            href={`mailto:${lead.email}`}
            target="_blank"
            rel="noreferrer"
          >
            Escribir email
          </Button>
        )}
        {lead.phone && (
          <Button icon={<PhoneOutlined />} href={`tel:${lead.phone}`}>
            Llamar
          </Button>
        )}
      </div>

      <div className="page-lead-detail__grid">
        <Card title="Contacto" size="small">
          <Field label="Nombre">{lead.name}</Field>
          <Field label="Email">
            <a href={`mailto:${lead.email}`}>{lead.email}</a>
          </Field>
          <Field label="Telefono">{lead.phone}</Field>
          <Field label="Rol">{lead.role}</Field>
        </Card>

        <Card title="Centro" size="small">
          <Field label="Centro">{lead.centerName}</Field>
          <Field label="Profesores aprox.">{lead.teachersEstimate}</Field>
          <Field label="Asignado a">
            <Select
              value={lead.assigned_to?._id || ""}
              onChange={(v) => handleAssignChange(v || null)}
              style={{ width: "100%" }}
              size="small"
              options={[
                { value: "", label: "Sin asignar" },
                ...assignableUsers.map((u) => ({
                  value: u._id,
                  label: u.name || u.email,
                })),
              ]}
            />
          </Field>
        </Card>

        <Card title="Atribucion (UTMs)" size="small">
          {hasUtm ? (
            <>
              <Field label="utm_source">{utm.source}</Field>
              <Field label="utm_medium">{utm.medium}</Field>
              <Field label="utm_campaign">{utm.campaign}</Field>
              <Field label="utm_content">{utm.content}</Field>
              <Field label="utm_term">{utm.term}</Field>
            </>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Sin UTMs"
            />
          )}
          <Divider style={{ margin: "12px 0" }} />
          <Field label="Referrer">
            {lead.referrer ? (
              <code style={{ fontSize: 12 }}>{lead.referrer}</code>
            ) : null}
          </Field>
          <Field label="Landing">
            {lead.landing ? (
              <code style={{ fontSize: 12 }}>{lead.landing}</code>
            ) : null}
          </Field>
          <Field label="IP / UA">
            <code style={{ fontSize: 11 }}>
              {[lead.ip, lead.userAgent].filter(Boolean).join(" / ")}
            </code>
          </Field>
        </Card>
      </div>

      {lead.message && (
        <Card title="Mensaje del lead" size="small" style={{ marginTop: 16 }}>
          <div style={{ whiteSpace: "pre-wrap", color: "#334155" }}>
            {lead.message}
          </div>
        </Card>
      )}

      <Card
        title={`Correos de nurture (${emails.length})`}
        size="small"
        style={{ marginTop: 16 }}
      >
        {dripState &&
          (dripState.hasUser ? (
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              <Tag color={dripState.eligible ? "green" : "default"}>
                {dripState.eligible ? "Elegible para drip" : "No elegible"}
              </Tag>
              <Tag>Cuenta: {dripState.account || "—"}</Tag>
              {/*
                Informativo: el drip son correos del propio servicio (soft
                opt-in) y NO depende de esta casilla; el opt-in aplica a
                promos/newsletter. Baja y pausa si bloquean.
              */}
              <Tag
                color={dripState.email_marketing_consent ? "green" : "default"}
              >
                {dripState.email_marketing_consent
                  ? "Opt-in promos"
                  : "Sin opt-in promos"}
              </Tag>
              {dripState.drip_paused && <Tag color="orange">Drip pausado</Tag>}
              {dripState.unsubscribed_at && (
                <Tag color="red">
                  Baja {dayjs(dripState.unsubscribed_at).format("DD/MM/YYYY")}
                </Tag>
              )}
            </div>
          ) : (
            <div style={{ marginBottom: 12 }}>
              <Tag>Sin cuenta asociada — el drip no aplica a este lead</Tag>
            </div>
          ))}

        {dripState?.hasUser && (
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
              marginBottom: 16,
            }}
          >
            <Button size="small" onClick={handleTogglePause} loading={pausing}>
              {dripState.drip_paused ? "Reanudar drip" : "Pausar drip"}
            </Button>
            {!dripState.email_marketing_consent &&
              !dripState.unsubscribed_at && (
                <Popconfirm
                  title="Activar el opt-in de promociones?"
                  description="El drip de la prueba ya sale solo (correos del servicio). Esto habilita ADEMAS promos y newsletter; usalo solo si el lead lo acepto por otro canal."
                  okText="Activar"
                  cancelText="Cancelar"
                  onConfirm={handleActivateConsent}
                >
                  <Button size="small" loading={consenting}>
                    Activar opt-in promos
                  </Button>
                </Popconfirm>
              )}
            <Divider type="vertical" style={{ height: 24 }} />
            <Select
              size="small"
              value={selectedStep}
              onChange={setSelectedStep}
              style={{ minWidth: 200 }}
              placeholder="Paso a enviar"
              options={steps.map((s) => ({
                value: s.step,
                label: `${s.step} · ${STEP_LABELS[s.key] || s.key}`,
              }))}
            />
            <Popconfirm
              title="Enviar este correo ahora?"
              description="Se enviara el correo real al lead. Ignora el interruptor global pero respeta consentimiento y baja."
              okText="Enviar"
              cancelText="Cancelar"
              onConfirm={handleSendNow}
              disabled={
                !selectedStep ||
                !dripState.email_marketing_consent ||
                !!dripState.unsubscribed_at
              }
            >
              <Button
                size="small"
                type="primary"
                icon={<SendOutlined />}
                loading={sending}
                disabled={
                  !selectedStep ||
                  !dripState.email_marketing_consent ||
                  !!dripState.unsubscribed_at
                }
              >
                Enviar ahora
              </Button>
            </Popconfirm>
          </div>
        )}

        {emailsLoading ? (
          <div style={{ textAlign: "center", padding: 16 }}>
            <Spin />
          </div>
        ) : emails.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Sin correos enviados todavia"
          />
        ) : (
          <Table
            columns={EMAIL_COLUMNS}
            dataSource={emails}
            rowKey={(r) => r._id}
            size="small"
            pagination={false}
          />
        )}
      </Card>

      <Card
        title={`Notas internas (${notesDesc.length})`}
        size="small"
        style={{ marginTop: 16 }}
      >
        <div className="lead-note-add">
          <TextArea
            rows={3}
            placeholder="Anadir nota interna (visible solo para el equipo)..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            maxLength={2000}
            showCount
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleAddNote}
            loading={sendingNote}
            disabled={!noteText.trim()}
            style={{ marginTop: 8 }}
          >
            Anadir nota
          </Button>
        </div>

        <Divider />

        {notesDesc.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Sin notas todavia"
          />
        ) : (
          <ul className="lead-notes-list">
            {notesDesc.map((n, idx) => (
              <li key={`${n.created_at}-${idx}`} className="lead-note">
                <div className="lead-note__meta">
                  <strong>{n.author || "—"}</strong>
                  <span>{dayjs(n.created_at).format("DD/MM/YYYY HH:mm")}</span>
                </div>
                <div className="lead-note__text">{n.text}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
